import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';

const UploadContext = createContext();

export const useUpload = () => {
  const context = useContext(UploadContext);
  if (!context) {
    throw new Error('useUpload must be used within an UploadProvider');
  }
  return context;
};

export const UploadProvider = ({ children }) => {
  // Load from sessionStorage on mount
  const [backgroundUploads, setBackgroundUploads] = useState(() => {
    try {
      const saved = sessionStorage.getItem('mydocs_background_uploads');
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('Error loading uploads from sessionStorage:', error);
      return [];
    }
  });

  const uploadPromisesRef = useRef({});

  // Save to sessionStorage whenever uploads change
  useEffect(() => {
    try {
      if (backgroundUploads.length > 0) {
        sessionStorage.setItem('mydocs_background_uploads', JSON.stringify(backgroundUploads));
      } else {
        sessionStorage.removeItem('mydocs_background_uploads');
      }
    } catch (error) {
      console.error('Error saving uploads to sessionStorage:', error);
    }
  }, [backgroundUploads]);

  // Auto-cleanup: Remove completed/error uploads after a delay (security & memory)
  useEffect(() => {
    const CLEANUP_DELAY = 1000; // 1 second after completion
    const timers = [];

    backgroundUploads.forEach(upload => {
      // Only cleanup completed or error uploads
      if (upload.status === 'completed' || upload.status === 'error') {
        const completionTime = upload.completionTime || Date.now();
        const timeSinceCompletion = Date.now() - completionTime;
        const remainingTime = Math.max(0, CLEANUP_DELAY - timeSinceCompletion);

        const timer = setTimeout(() => {
          setBackgroundUploads(prev => {
            const filtered = prev.filter(u => u.id !== upload.id);
            // Clean up promise ref
            if (uploadPromisesRef.current[upload.id]) {
              delete uploadPromisesRef.current[upload.id];
            }
            return filtered;
          });
        }, remainingTime);

        timers.push(timer);
      }
    });

    // Cleanup timers on unmount or when uploads change
    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
  }, [backgroundUploads]);

  const removeBackgroundUpload = useCallback((uploadId) => {
    setBackgroundUploads(prev => prev.filter(u => u.id !== uploadId));
    // Clean up promise ref
    if (uploadPromisesRef.current[uploadId]) {
      delete uploadPromisesRef.current[uploadId];
    }
  }, []);

  const clearCompletedUploads = useCallback(() => {
    setBackgroundUploads(prev => {
      const uploading = prev.filter(u => u.status === 'uploading');
      // Clean up promise refs for completed uploads
      prev.forEach(u => {
        if (u.status !== 'uploading' && uploadPromisesRef.current[u.id]) {
          delete uploadPromisesRef.current[u.id];
        }
      });
      return uploading;
    });
  }, []);

  const getActiveUploadsCount = useCallback(() => {
    return backgroundUploads.filter(u => u.status === 'uploading').length;
  }, [backgroundUploads]);

  const hasActiveUploads = useCallback(() => {
    return backgroundUploads.some(u => u.status === 'uploading');
  }, [backgroundUploads]);

  const value = {
    backgroundUploads,
    setBackgroundUploads,
    uploadPromisesRef,
    removeBackgroundUpload,
    clearCompletedUploads,
    getActiveUploadsCount,
    hasActiveUploads,
  };

  return (
    <UploadContext.Provider value={value}>
      {children}
    </UploadContext.Provider>
  );
};
